import { assign, Machine } from 'xstate';
import { isProduction } from '@pypestream/utils';

import {
  GLOBAL_APP_SUB_MACHINE_IDS,
  sendUserMessage,
  showToastMessage,
} from './app.xstate-utils';
import { getXstateUtils } from './xstate.utils';
import { userService } from './app.xstate';
import {
  ProStudioContext,
  ProStudioEvents,
  ProStudioTypestates,
  ProStudioState,
} from './prostudio.xstate.types';
import {
  changeBotType,
  createBot,
  deleteAsset,
  deleteBot,
  downloadVersionFile,
  loadBots,
  loadBotVersions,
  publishBotVersion,
  deleteScript,
  getFileData,
  validateVersion,
  loadBotPypes,
  deleteChannel,
  createChannel,
  updateChannel,
  getProjectData,
  getUserData,
  updateAssets,
  updateSolution,
  refetchSolutionData,
  loadResourceLibraryData,
  uploadCsvFile,
  initFeatureFlags,
  setSelectedSolution,
  identifyLaunchDarklyContext,
  updateSolutionName,
} from './prostudio.xstate.invokes';
import { downloadPythonFile } from '../utils';
import { ErrorTags } from '../components/error-boundaries/types';
import { getAnalytics } from '../hooks/use-analytics';
import { tableStore, ValidationMode } from '../stores';

const {
  createInvokablePromise,
  createXstateHooks: createProStudioXstateHooks,
} = getXstateUtils<
  ProStudioContext,
  ProStudioEvents,
  ProStudioTypestates,
  ProStudioState
>();

export { createProStudioXstateHooks };

const entryLogsAvalable = !isProduction;

const logOnEntry = ({
  name,
  info,
  color = 'purple',
}: {
  name: string;
  info?: unknown;
  color?: string;
}) => {
  if (!entryLogsAvalable) return;
  console.log(
    `%cstate: ${name}`,
    `color: ${color}; font-size: larger; font-weight: bold`,
    info || ''
  );
};

const initialBotRelatedContext = {
  botId: undefined,
  botName: undefined,
  botIdURLParam: undefined,
  channelId: undefined,
  deployedVersions: null,
  draft: null,
  draftVersion: null,
  envs: undefined,
  botPypes: null,
  selectedBot: undefined,
  draftVersionSolutionData: null,
  solutionVersions: [],
  corpusSettings: null,
  // initial state for versions should be undefined (not empty array) because we are not loading the data yet
  versions: undefined,
};

const initialContext: ProStudioContext = {
  ...initialBotRelatedContext,
  customerId: undefined,
  credentials: undefined,
  providers: undefined,
  project: undefined,
  orgName: 'Pypestream',
  isSwitchingSolution: false,
  // initial state for bots should be undefined (not empty array) because we are not loading the data yet
  solutions: undefined,
  userInfo: {},
  userProjects: undefined,
  projectUsers: undefined,
  errors: [],
  debugMode: false,
  presencePagePath: undefined,
  presenceSolutionId: undefined,
  solutionUserPresences: undefined,
};

export const proStudioMachine = Machine<
  ProStudioContext,
  ProStudioState,
  ProStudioEvents
>({
  id: GLOBAL_APP_SUB_MACHINE_IDS.proStudio,
  predictableActionArguments: true,
  preserveActionOrder: true,
  context: initialContext,
  invoke: [
    {
      id: 'initializing-feature-flags',
      src: initFeatureFlags,
    },
    {
      id: 'prostudioObserver',
      src: getUserData,
    },
    {
      id: 'identifyingLaunchDarklyContext',
      src: identifyLaunchDarklyContext,
    },
  ],
  on: {
    setFeatureFlags: {
      actions: assign((ctx, { values }) => {
        ctx.featureFlags = values;
        return ctx;
      }),
    },
    updateUserInfo: {
      target: '#checkingUserInfo',
      actions: assign((ctx, event) => {
        logOnEntry({
          name: 'updateUserInfo',
          info: { ctx, event },
          color: 'cyan',
        });
        const {
          data: { userProjects, ...userInfo },
        } = event;
        ctx.userInfo = userInfo;
        ctx.userProjects = userProjects;
        return ctx;
      }),
    },
    setParams: [
      {
        actions: assign((ctx, event) => {
          if (event.type !== 'setParams') {
            return ctx;
          }

          logOnEntry({
            name: 'setParams',
            info: { ctx, event },
            color: 'cyan',
          });

          return {
            ...ctx,
            project: {
              ...ctx.project,
              id: event.payload.projectId || ctx.project?.id || '',
              name: ctx.project?.name || '',
              createdAt: ctx.project?.createdAt || '',
              accountId: ctx.project?.accountId || '',
            },
            botName: event.payload.botName || ctx.botName || '',
            channelId: event.payload.channelId || ctx.channelId,
            debugMode: event.payload.debugMode || ctx.debugMode,
            botIdURLParam:
              event.payload.botIdURLParam || ctx.botIdURLParam || '',
          };
        }),
        target: `#checkingUserInfo`,
        cond: (ctx, event) =>
          !ctx.isSwitchingSolution &&
          (!!event.payload.botName ||
            !!event.payload.projectId ||
            !!event.payload.channelId),
      },
      {
        actions: assign((ctx, event) => {
          if (event.type !== 'setParams') {
            return ctx;
          }

          return {
            ...ctx,
            project: {
              ...ctx.project,
              id: event.payload.projectId || ctx.project?.id || '',
              name: ctx.project?.name || '',
              createdAt: ctx.project?.createdAt || '',
              accountId: ctx.project?.accountId || '',
            },
            botName: event.payload.botName || ctx.botName || '',
            channelId: event.payload.channelId || ctx.channelId,
            debugMode: event.payload.debugMode || ctx.debugMode,
            botIdURLParam:
              event.payload.botIdURLParam || ctx.botIdURLParam || '',
          };
        }),
        cond: (ctx, event) =>
          !!ctx.isSwitchingSolution &&
          (!!event.payload.botName ||
            !!event.payload.projectId ||
            !!event.payload.channelId),
      },
    ],
    showError: [
      {
        actions: [
          (ctx, event) => {
            logOnEntry({
              name: 'showError',
              info: { ctx, event },
              color: 'cyan',
            });
            sendUserMessage({
              type: 'error',
              text: event.error,
              autoClose: 3000,
              position: 'top-right',
            });
          },
        ],
      },
    ],
    startPresence: {
      actions: assign((_ctx, event) => {
        if (event.type !== 'startPresence') return {};
        return {
          presenceSolutionId: event.solutionId,
          presencePagePath: event.pagePath,
        };
      }),
    },
    stopPresence: {
      actions: assign({
        presenceSolutionId: undefined,
        presencePagePath: undefined,
        solutionUserPresences: undefined,
      }),
    },
    presenceRouteChange: {
      actions: assign((_ctx, event) => {
        if (event.type !== 'presenceRouteChange') return {};
        return { presencePagePath: event.pagePath };
      }),
    },
    setPresenceData: {
      actions: assign((_ctx, event) => {
        if (event.type !== 'setPresenceData') return {};
        return { solutionUserPresences: event.presences };
      }),
    },
  },
  states: {
    studio_idle: {
      id: 'studio_idle',
      entry: (ctx, event) => {
        logOnEntry({
          name: 'studio_idle',
          info: { ctx, event },
        });
      },
    },
    checkingUserInfo: {
      id: 'checkingUserInfo',
      initial: 'checkAccess',
      entry: (ctx, event) => {
        logOnEntry({
          name: 'checkingUserInfo',
          info: { ctx, event },
        });
      },
      states: {
        checkAccess: {
          id: 'checkAccess',
          entry: (ctx, event) => {
            logOnEntry({
              name: 'checkAccess',
              info: { ctx, event },
            });
          },
          always: [
            {
              target: '#accessDenied',
              cond: (ctx, _event) => {
                const projectId = ctx.project?.id;

                const res =
                  Boolean(
                    ctx.userProjects !== undefined &&
                      projectId &&
                      !ctx.userProjects?.map(({ id }) => id).includes(projectId)
                  ) ||
                  Boolean(
                    ctx.userProjects && !ctx.userProjects.length && !projectId
                  );

                return res;
              },
            },
            {
              target: '#projectFlow',
              cond: (ctx, _event) =>
                Boolean(
                  ctx.customerId === undefined &&
                    ctx.userInfo?.id !== undefined &&
                    ctx.userProjects &&
                    ctx.project?.id
                ),
            },
            {
              target: '#botFlow',
              cond: (ctx, _event) =>
                !!ctx.userInfo?.id &&
                !!ctx.customerId &&
                !!ctx.project?.id &&
                !!ctx.botName,
            },
            {
              target: '#studio_idle',
              cond: (ctx, _event) => !ctx.userInfo?.id || !ctx.customerId,
            },
          ],
        },
        projectFlow: {
          id: 'projectFlow',
          initial: 'loadingProjectData',
          entry: (ctx, event) => {
            logOnEntry({
              name: 'projectFlow',
              info: { ctx, event },
            });
          },
          states: {
            loadingProjectData: {
              id: 'loadingProjectData',
              entry: (ctx, event) => {
                logOnEntry({
                  name: 'loadingProjectData',
                  info: { ctx, event },
                });
              },
              invoke: createInvokablePromise({
                src: getProjectData,
                onDoneTarget: '#validatingProjectId',
                onErrorTarget: '#error',
                onDoneAssignContext({ ctx, data }) {
                  if (data) {
                    ctx.customerId = data.id;
                    ctx.project = data.project;
                    ctx.credentials = data.credentials;
                    ctx.providers = data.providers;
                    ctx.projectUsers = data.projectUsers;
                  }
                },
                onErrorAssignContext({ ctx, error }) {
                  ctx.errors?.push(error);
                },
                onErrorActions: [
                  showToastMessage({
                    text: 'Error fetching customer data',
                  }),
                ],
              }),
            },
            validatingProjectId: {
              id: 'validatingProjectId',
              entry: (ctx, event) => {
                logOnEntry({
                  name: 'validatingProjectId',
                  info: { ctx, event },
                });
              },
              always: [
                {
                  target: '#accessDenied',
                  cond: (ctx, _event) => {
                    const projectId = ctx.project?.id;
                    const res =
                      Boolean(
                        ctx.userProjects !== undefined &&
                          projectId &&
                          !ctx.userProjects
                            ?.map(({ id }) => id)
                            .includes(projectId)
                      ) ||
                      Boolean(
                        ctx.userProjects &&
                          !ctx.userProjects.length &&
                          !projectId
                      );
                    return res;
                  },
                },
                {
                  target: '#projectDataLoaded',
                  cond: (ctx, _event) =>
                    Boolean(ctx.userInfo?.id !== undefined && ctx.userProjects),
                },
              ],
            },
            projectDataLoaded: {
              id: 'projectDataLoaded',
              initial: 'checkingRequiredData',
              entry: (ctx, event) => {
                logOnEntry({
                  name: 'projectDataLoaded',
                  info: { ctx, event },
                });
              },
              states: {
                checkingRequiredData: {
                  id: 'checkingRequiredData',
                  entry: (ctx, event) => {
                    logOnEntry({
                      name: 'checkingRequiredData',
                      info: { ctx, event },
                    });
                  },
                  always: [
                    {
                      target: '#loadingBots',
                      cond: (ctx, _event) => !ctx.solutions,
                    },
                    {
                      target: '#botFlow',
                      cond: (ctx, _event) =>
                        !!ctx.botName &&
                        !ctx.draft &&
                        !ctx.versions &&
                        !ctx.deployedVersions &&
                        !ctx.selectedBot &&
                        !ctx.envs,
                    },
                    {
                      actions: [
                        assign((ctx, event) => {
                          logOnEntry({
                            name: 'setBotName',
                            info: { ctx, event },
                            color: 'cyan',
                          });

                          const botName = ctx.solutions?.find(
                            (solution) => solution.id === ctx.botIdURLParam
                          )?.name;

                          return {
                            ...ctx,
                            botName,
                            botIdURLParam: undefined,
                          };
                        }),
                      ],
                      cond: (ctx, _event) =>
                        !ctx.botName && !!ctx.solutions && !!ctx.botIdURLParam,
                      target: '#projectDataLoaded',
                    },
                    {
                      target: '#idle',
                    },
                  ],
                },
                idle: {
                  id: 'idle',
                  initial: 'idleView',
                  entry: [
                    (ctx, event) => {
                      logOnEntry({
                        name: 'idle',
                        info: { ctx, event },
                        color: 'green',
                      });
                    },
                    assign({ isSwitchingSolution: false }),
                  ],
                  on: {
                    selectBot: [
                      {
                        actions: [
                          assign((ctx, event) => {
                            logOnEntry({
                              name: 'selectBot',
                              info: { ctx, event },
                              color: 'cyan',
                            });

                            return {
                              ...ctx,
                              ...initialBotRelatedContext,
                              isSwitchingSolution: true,
                            };
                          }),
                        ],
                        cond: (ctx, event) =>
                          ctx.selectedBot?.displayName !== event.name,
                        target: '#setSelectedSolution',
                      },
                      {
                        actions: [
                          assign((ctx, event) => {
                            logOnEntry({
                              name: 'selectBot',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                            if (event.cb && event.id) {
                              event.cb(`/${ctx.project?.id}/${event.id}`);
                            }

                            return ctx;
                          }),
                        ],
                        cond: (ctx, event) =>
                          ctx.selectedBot?.displayName === event.name,
                        target: '#idle',
                      },
                    ],
                    createCustomerBot: [
                      {
                        target: '#creatingBotFlow',
                        actions: [
                          assign((ctx, event) => {
                            logOnEntry({
                              name: 'createCustomerBot',
                              info: { ctx, event },
                              color: 'cyan',
                            });

                            return {
                              ...ctx,
                              ...initialBotRelatedContext,
                            };
                          }),
                        ],
                      },
                    ],
                    deleteBot: [
                      {
                        target: '#deletingBotFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'deleteBot',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    publishBot: [
                      {
                        target: '#publishingBotFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'publishBot',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    setBotType: [
                      {
                        target: '#updatingBotFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'setBotType',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    updateSolution: [
                      {
                        target: '#updatingSolutionFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'updateSolution',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    refetchSolutionData: [
                      {
                        target: '#refetchingSolutionDataFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'refetchSolutionData',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    getVersionFile: [
                      {
                        target: '#downloadingVersionFileFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'getVersionFile',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    deleteAsset: [
                      {
                        target: '#deleteAssetFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'deleteAsset',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    copyToClipboard: [
                      {
                        actions: [
                          async (ctx, event) => {
                            logOnEntry({
                              name: 'copyToClipboard',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                            await navigator.clipboard.writeText(event.value);

                            sendUserMessage({
                              type: 'success',
                              text: event.message,
                              autoClose: 3000,
                              position: 'top-right',
                            });
                          },
                        ],
                      },
                    ],
                    updateAssets: [
                      {
                        actions: [
                          assign((ctx, event) => {
                            logOnEntry({
                              name: 'updateAssets',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                            return {
                              ...ctx,
                              selectedBot: event.data,
                            };
                          }),
                        ],
                        target: '#updatingAssetsFlow',
                      },
                    ],
                    updateDraftData: [
                      {
                        target: '#idle',
                        actions: [
                          assign((ctx, event) => {
                            logOnEntry({
                              name: 'updateDraftData',
                              info: { ctx, event },
                              color: 'cyan',
                            });

                            sendUserMessage({
                              type: 'success',
                              text: `Script ${
                                event.action === 'update'
                                  ? 'updated'
                                  : 'uploaded'
                              } successfully`,
                              autoClose: 3000,
                              position: 'top-right',
                            });

                            return {
                              ...ctx,
                              draft: event.draft,
                            };
                          }),
                        ],
                      },
                    ],
                    deleteScript: {
                      target: '#deletingScriptFlow',
                      actions: [
                        (ctx, event) => {
                          logOnEntry({
                            name: 'deleteScript',
                            info: { ctx, event },
                            color: 'cyan',
                          });
                        },
                      ],
                    },
                    editScript: [
                      {
                        target: `#loadingScriptFlow`,
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'editScript',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    downloadScript: [
                      {
                        target: `#loadingScriptFlow`,
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'downloadScript',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    createChannel: [
                      {
                        target: '#creatingChannelFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'createChannel',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    updateChannel: [
                      {
                        target: '#updatingChannelFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'updateChannel',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    deleteChannel: [
                      {
                        target: '#deletingChannelFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'deleteChannel',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    uploadCsvFile: [
                      {
                        target: '#uploadingCsvFileFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'uploadingCsvFileFlow',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                    updateSolutionName: [
                      {
                        target: '#updatingSolutionNameFlow',
                        actions: [
                          (ctx, event) => {
                            logOnEntry({
                              name: 'updateSolutionName',
                              info: { ctx, event },
                              color: 'cyan',
                            });
                          },
                        ],
                      },
                    ],
                  },
                  states: {
                    idleView: {},
                  },
                },
                loadingBots: {
                  id: 'loadingBots',
                  entry: (ctx, event) => {
                    logOnEntry({
                      name: 'loadingBots',
                      info: { ctx, event },
                    });
                  },
                  invoke: createInvokablePromise({
                    src: loadBots,
                    onDoneTarget: '#checkingRequiredData',
                    onErrorTarget: '#error',
                    onDoneAssignContext({ ctx, data }) {
                      if (data) {
                        ctx.solutions =
                          data.solutions as ProStudioContext['solutions'];
                      }
                    },
                    onErrorAssignContext({ ctx, error }) {
                      ctx.errors?.push(error);
                    },
                    onErrorActions: [
                      showToastMessage({
                        text: 'Error fetching solutions and customer pypes data',
                      }),
                    ],
                  }),
                },
                botFlow: {
                  id: 'botFlow',
                  initial: 'botDataLoading',
                  entry: (ctx, event) => {
                    logOnEntry({
                      name: 'botFlow',
                      info: { ctx, event },
                    });
                  },
                  states: {
                    botDataLoading: {
                      id: 'botDataLoading',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'botDataLoading',
                          info: { ctx, event },
                        });
                      },
                      type: 'parallel',
                      onDone: {
                        target: '#validatingDraftFlow',
                      },
                      states: {
                        loadingResourceLibraryData: {
                          id: 'loadingResourceLibraryData',
                          initial: 'loading',
                          entry: (ctx, event) => {
                            logOnEntry({
                              name: 'loadingResourceLibraryData',
                              info: { ctx, event },
                            });
                          },
                          states: {
                            loading: {
                              entry: (ctx, event) => {
                                logOnEntry({
                                  name: 'loadingResourceLibraryData.loading',
                                  info: { ctx, event },
                                });
                              },
                              invoke: createInvokablePromise({
                                src: loadResourceLibraryData,
                                onDoneTarget: '#loadedResourceLibraryData',
                                onErrorTarget: '#botFlowError',
                                onErrorAssignContext({ ctx, error }) {
                                  ctx.errors?.push(error);
                                },
                                onDoneAssignContext({ ctx, data }) {
                                  if (data) {
                                    ctx.resourceLibraryData =
                                      data.resourceLibraryData;
                                  }
                                },
                                onErrorActions: [
                                  showToastMessage({
                                    text: 'Error fetching resource library data',
                                  }),
                                ],
                              }),
                            },
                            loaded: {
                              id: 'loadedResourceLibraryData',
                              type: 'final',
                              entry: (ctx, event) => {
                                logOnEntry({
                                  name: 'loadedResourceLibraryData.loaded',
                                  info: { ctx, event },
                                });
                              },
                            },
                          },
                        },
                        loadingVersions: {
                          id: 'loadingVersions',
                          initial: 'loading',
                          entry: (ctx, event) => {
                            logOnEntry({
                              name: 'loadingVersions',
                              info: { ctx, event },
                            });
                          },
                          states: {
                            loading: {
                              entry: (ctx, event) => {
                                logOnEntry({
                                  name: 'loadingVersions.loading',
                                  info: { ctx, event },
                                });
                              },
                              invoke: createInvokablePromise({
                                src: loadBotVersions,
                                onDoneTarget: '#loadedVersions',
                                onErrorTarget: '#botFlowError',
                                onErrorAssignContext({ ctx, error }) {
                                  ctx.errors?.push(error);
                                },
                                onDoneAssignContext({ ctx, data }) {
                                  if (data) {
                                    const {
                                      versions,
                                      deployedVersions,
                                      draftVersion,
                                      envs,
                                      selectedBot,
                                      draftData,
                                      draftVersionSolutionData,
                                      solutionVersions,
                                      corpusSettings,
                                    } = data;

                                    ctx.draft = draftData;
                                    ctx.versions = versions || [];
                                    ctx.deployedVersions = deployedVersions;
                                    ctx.draftVersion = draftVersion;
                                    ctx.envs = envs;
                                    ctx.selectedBot = selectedBot;
                                    if (ctx.solutions) {
                                      ctx.solutions = ctx.solutions.map(
                                        (bot) =>
                                          bot.botSolutionId === selectedBot.id
                                            ? {
                                                ...selectedBot,
                                                ...bot,
                                              }
                                            : bot
                                      );
                                    }
                                    ctx.draftVersionSolutionData =
                                      draftVersionSolutionData as ProStudioContext['draftVersionSolutionData'];
                                    ctx.solutionVersions =
                                      solutionVersions as ProStudioContext['solutionVersions'];
                                    ctx.corpusSettings = corpusSettings;
                                  }
                                },
                                onErrorActions: [
                                  showToastMessage({
                                    text: 'Error fetching solutions data',
                                  }),
                                ],
                              }),
                            },
                            loaded: {
                              id: 'loadedVersions',
                              type: 'final',
                              entry: (ctx, event) => {
                                logOnEntry({
                                  name: 'loadingVersions.loaded',
                                  info: { ctx, event },
                                });
                              },
                            },
                          },
                        },
                        loadingPypes: {
                          id: 'loadingPypes',
                          initial: 'loading',
                          entry: (ctx, event) => {
                            logOnEntry({
                              name: 'loadingPypes',
                              info: { ctx, event },
                            });
                          },
                          states: {
                            loading: {
                              entry: (ctx, event) => {
                                logOnEntry({
                                  name: 'loadingPypes.loading',
                                  info: { ctx, event },
                                });
                              },
                              invoke: createInvokablePromise({
                                src: loadBotPypes,
                                onDoneTarget: '#loadedPypes',
                                onErrorTarget: '#botFlowError',
                                onErrorAssignContext({ ctx, error }) {
                                  ctx.errors?.push(error);
                                },
                                onDoneAssignContext({ ctx, data }) {
                                  if (data) {
                                    ctx.botPypes = data.botPypes;
                                  }
                                },
                                onErrorActions: [
                                  showToastMessage({
                                    text: 'Error loading pypes',
                                  }),
                                ],
                              }),
                            },
                            loaded: {
                              id: 'loadedPypes',
                              entry: (ctx, event) => {
                                logOnEntry({
                                  name: 'loadingPypes.loaded',
                                  info: { ctx, event },
                                });
                              },
                              type: 'final',
                            },
                          },
                        },
                      },
                    },
                    setSelectedSolution: {
                      id: 'setSelectedSolution',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'setSelectedSolution',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: setSelectedSolution,
                        onDoneTarget: `#botFlow`,
                        onErrorTarget: '#botFlowError',
                        onDoneAssignContext({ ctx, data }) {
                          if (!data) {
                            return ctx;
                          }

                          ctx.selectedBot = data.selectedSolution;
                          ctx.botId = data.selectedSolution?.id;
                          ctx.botName = data.selectedSolution?.botName;
                          data.cb?.();
                        },
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error setting selected bot',
                          }),
                        ],
                      }),
                    },
                    validatingDraftFlow: {
                      id: 'validatingDraftFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'validatingDraftFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: validateVersion,
                        onDoneTarget: `#botDataLoaded`,
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error validating draft',
                          }),
                        ],
                      }),
                    },
                    botDataLoaded: {
                      id: 'botDataLoaded',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'botDataLoaded',
                          info: { ctx, event },
                        });
                      },
                      always: [
                        {
                          target: '#checkingRequiredData',
                        },
                      ],
                    },
                    creatingBotFlow: {
                      id: 'creatingBotFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'creatingBotFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: createBot,
                        onDoneTarget: '#botFlow',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            // ctx.solutions = undefined;
                            ctx.botName = data.botName;
                            ctx.selectedBot = data.selectedSolution;
                            ctx.solutions =
                              data.solutions as ProStudioContext['solutions'];

                            data.cb(
                              `/${ctx.project?.id}/${data.selectedSolution.uniqueId}`
                            );
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Solution created successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error creating solution',
                          }),
                        ],
                      }),
                    },
                    deletingBotFlow: {
                      id: 'deletingBotFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'deletingBotFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: deleteBot,
                        onDoneTarget: '#loadingBots',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            data.cb?.();

                            ctx.solutions = undefined;
                            ctx.botName = undefined;
                            ctx.channelId = undefined;
                            ctx.deployedVersions = null;
                            ctx.draft = null;
                            ctx.draftVersion = null;
                            ctx.envs = undefined;
                            ctx.botPypes = null;
                            ctx.selectedBot = undefined;
                            ctx.draftVersionSolutionData = null;
                            ctx.solutionVersions = [];
                            ctx.corpusSettings = null;
                            ctx.versions = undefined;
                            ctx.botIdURLParam = undefined;
                            ctx.botId = undefined;
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Solution deleted successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error deleting solution',
                          }),
                        ],
                      }),
                    },
                    publishingBotFlow: {
                      id: 'publishingBotFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'publishingBotFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: publishBotVersion,
                        onDoneTarget: '#botDataLoading',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onDoneAssignContext({ ctx }) {
                          ctx.selectedBot = undefined;
                        },
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error publishing solution version',
                          }),
                        ],
                      }),
                    },
                    updatingBotFlow: {
                      id: 'updatingBotFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'updatingBotFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: changeBotType,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onDoneAssignContext({ ctx, data: patchedBot }) {
                          if (patchedBot) {
                            ctx.selectedBot = patchedBot;

                            ctx.solutions = ctx.solutions?.map((bot) =>
                              bot.botSolutionId === patchedBot.id
                                ? {
                                    ...patchedBot,
                                    ...bot,
                                  }
                                : bot
                            );
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Solution type updated successfully',
                            type: 'success',
                            position: 'top-center',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error updating solution type',
                            position: 'top-center',
                          }),
                        ],
                      }),
                    },
                    updatingSolutionFlow: {
                      id: 'updatingSolutionFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'updatingSolutionFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: updateSolution,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.draftVersionSolutionData =
                              data.draftVersionSolutionData as ProStudioContext['draftVersionSolutionData'];
                            if (data.cb) {
                              data.cb(
                                data.draftVersionSolutionData as ProStudioContext['draftVersionSolutionData']
                              );
                            }
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Solution updated successfully',
                            type: 'success',
                            position: 'top-center',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error updating solution',
                            position: 'top-center',
                          }),
                        ],
                      }),
                    },
                    refetchingSolutionDataFlow: {
                      id: 'refetchingSolutionDataFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'refetchingSolutionDataFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: refetchSolutionData,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.draftVersionSolutionData =
                              data.draftVersionSolutionData as ProStudioContext['draftVersionSolutionData'];
                            if (data.cb) {
                              data.cb(
                                data.draftVersionSolutionData as ProStudioContext['draftVersionSolutionData']
                              );
                            }
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Solution data refreshed',
                            type: 'success',
                            position: 'top-center',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error refreshing solution data',
                            position: 'top-center',
                          }),
                        ],
                      }),
                    },
                    downloadingVersionFileFlow: {
                      id: 'downloadingVersionFileFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'downloadingVersionFileFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: downloadVersionFile,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onDoneActions: [],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error downloading solution files',
                          }),
                        ],
                      }),
                    },
                    deleteAssetFlow: {
                      id: 'deleteAssetFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'deleteAssetFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: deleteAsset,
                        onDoneTarget: `#idle`,
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.selectedBot = data.selectedBot;
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Asset deleted successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error deleting asset',
                          }),
                        ],
                      }),
                    },
                    updatingAssetsFlow: {
                      id: 'updatingAssetsFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'updatingAssetsFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: updateAssets,
                        onDoneTarget: `#idle`,
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.selectedBot = data.selectedBot;
                          }
                          if (data?.cb && data.selectedBot?.assets) {
                            data.cb(
                              data.selectedBot.assets as {
                                id: string;
                                url: string;
                              }[]
                            );
                          }
                        },
                      }),
                    },
                    deletingScriptFlow: {
                      id: 'deletingScriptFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'deletingScriptFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: deleteScript,
                        onDoneTarget: `#idle`,
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.draft = data.draft;
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Script deleted successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error deleting script',
                          }),
                        ],
                      }),
                    },
                    loadingScriptFlow: {
                      id: 'loadingScriptFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'loadingScriptFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: getFileData,
                        onDoneTarget: `#idle`,
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onDoneActions: [
                          {
                            type: 'downloadScript',
                            exec(_ctx, event) {
                              if (!event.data) {
                                return;
                              }

                              console.log('event.data.type', event.data.type);

                              if (event.data.type === 'downloadScript') {
                                downloadPythonFile(
                                  event.data.fileData,
                                  event.data.fileName
                                );
                              }
                              if (
                                event.data.type === 'editScript' &&
                                event.data.cb
                              ) {
                                event.data.cb(event.data.fileData);
                              }
                            },
                          },
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error downloading script',
                          }),
                        ],
                      }),
                    },
                    creatingChannelFlow: {
                      id: 'creatingChannelFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'creatingChannelFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: createChannel,
                        onDoneTarget: `#idle`,
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data && 'botPypes' in data) {
                            ctx.botPypes = data.botPypes;
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Channel created successfully',
                            type: 'success',
                          }),
                          (ctx, event) => {
                            // Only track if user has consented to analytics
                            const analytics = getAnalytics();
                            if (
                              analytics &&
                              typeof analytics.has_opted_in_capturing ===
                                'function' &&
                              analytics.has_opted_in_capturing() &&
                              typeof analytics.capture === 'function' &&
                              'data' in event
                            ) {
                              const data = event.data as {
                                channelId?: string;
                                channelName?: string;
                                pypeId?: string;
                                pypeName?: string;
                              };
                              const userEmail =
                                userService.state.context.user?.email;

                              analytics.capture('channel_created', {
                                user_email: userEmail,
                                channel_id: data.channelId,
                                channel_name: data.channelName,
                                pype_id: data.pypeId,
                                pype_name: data.pypeName,
                                bot_name: ctx.botName,
                                project_id: ctx.project?.id,
                              });
                            }
                          },
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error creating channel',
                          }),
                        ],
                      }),
                    },
                    deletingChannelFlow: {
                      id: 'deletingChannelFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'deletingChannelFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: deleteChannel,
                        onDoneTarget: `#idle`,
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.botPypes = data.botPypes;
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Channel deleted successfully',
                            type: 'success',
                          }),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error deleting channel',
                          }),
                        ],
                      }),
                    },
                    updatingChannelFlow: {
                      id: 'updatingChannelFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'updatingChannelFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: updateChannel,
                        onDoneTarget: `#idle`,
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data && 'botPypes' in data) {
                            ctx.botPypes = data.botPypes;
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Channel updated successfully',
                            type: 'success',
                          }),
                          (ctx, event) => {
                            // Only track if user has consented to analytics
                            const analytics = getAnalytics();
                            if (
                              analytics &&
                              typeof analytics.has_opted_in_capturing ===
                                'function' &&
                              analytics.has_opted_in_capturing() &&
                              typeof analytics.capture === 'function' &&
                              'data' in event
                            ) {
                              const data = event.data as {
                                channelId?: string;
                                channelName?: string;
                                pypeId?: string;
                                pypeName?: string;
                              };
                              const userEmail =
                                userService.state.context.user?.email;

                              analytics.capture('channel_updated', {
                                user_email: userEmail,
                                channel_id: data.channelId,
                                channel_name: data.channelName,
                                pype_id: data.pypeId,
                                pype_name: data.pypeName,
                                bot_name: ctx.botName,
                                project_id: ctx.project?.id,
                              });
                            }
                          },
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error updating channel',
                          }),
                        ],
                      }),
                    },
                    updatingSolutionNameFlow: {
                      id: 'updatingSolutionNameFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'updatingSolutionNameFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: updateSolutionName,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onDoneAssignContext({ ctx, data }) {
                          if (data) {
                            ctx.selectedBot = data.selectedSolution;
                            if (data.solutions) {
                              ctx.solutions = data.solutions;
                            }
                          }
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'Solution name updated successfully',
                            type: 'success',
                          }),
                        ],
                      }),
                    },
                    uploadingCsvFileFlow: {
                      id: 'uploadingCsvFileFlow',
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'uploadingCsvFileFlow',
                          info: { ctx, event },
                        });
                      },
                      invoke: createInvokablePromise({
                        src: uploadCsvFile,
                        onDoneTarget: '#idle',
                        onErrorTarget: '#botFlowError',
                        onErrorAssignContext({ ctx, error }) {
                          ctx.errors?.push(error);
                        },
                        onDoneActions: [
                          showToastMessage({
                            text: 'CSV successfully imported',
                            type: 'success',
                          }),
                          () =>
                            tableStore.setValidationMode(ValidationMode.Manual),
                        ],
                        onErrorActions: [
                          showToastMessage({
                            text: 'Error uploading CSV file',
                          }),
                        ],
                      }),
                    },
                    botFlowError: {
                      id: 'botFlowError',
                      tags: [ErrorTags.xstate],
                      entry: (ctx, event) => {
                        logOnEntry({
                          name: 'botFlowError',
                          info: { ctx, event },
                          color: 'red',
                        });
                      },
                      exit: assign((ctx, _event) => {
                        return {
                          ...ctx,
                          errors: [],
                        };
                      }),
                      after: {
                        100: '#idle',
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
    accessDenied: {
      id: 'accessDenied',
      entry: (ctx, event) => {
        logOnEntry({
          name: 'accessDenied',
          info: { ctx, event },
        });
      },
    },
    error: {
      id: 'error',
      tags: [ErrorTags.xstate],
      entry: (ctx, event) => {
        logOnEntry({
          name: 'error',
          info: { ctx, event },
          color: 'red',
        });
      },
    },
  },
});
